/**
 * ProfileTab Component (Simplified)
 * 
 * Shows only profile card with key information.
 * All details are in the PDF report only.
 */
import React from 'react';
import { ResultData, ProfileReportData } from '../../types/results';

interface ProfileTabProps {
  results: ResultData;
  loadingProfile: boolean;
  profileReport: ProfileReportData | null;
  generatingPdf: boolean;
  onOpenPdfViewer: () => void;
  onDownloadPdf: () => void;
  onLoadProfile: () => void;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({
  results,
  loadingProfile,
  profileReport,
  generatingPdf,
  onOpenPdfViewer,
  onDownloadPdf,
  onLoadProfile
}) => {
  if (loadingProfile) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin inline-block w-10 h-10 border-3 border-primary border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-600 font-medium">Analyse de votre profil...</p>
      </div>
    );
  }

  if (!profileReport) {
    return (
      <div className="text-center py-12">
        <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <p className="text-gray-600 mb-4">Profil non encore analysé</p>
        <button
          onClick={onLoadProfile}
          className="bg-primary text-white font-semibold py-2.5 px-8 rounded-lg hover:bg-opacity-90 transition"
        >
          Analyser mon profil
        </button>
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* Profile Card */}
      <div className="bg-gradient-to-br from-primary to-blue-700 rounded-xl p-5 text-white">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 min-w-0">
            <p className="text-blue-200 text-xs uppercase tracking-wide mb-1">Votre profil</p>
            <h2 className="text-lg sm:text-xl font-bold leading-tight">{profileReport.profile_name}</h2>
            <p className="text-blue-200 text-xs sm:text-sm italic mt-1 truncate">"{profileReport.subtitle}"</p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg px-2 sm:px-3 py-1.5 ml-2 flex-shrink-0">
            <span className="text-base sm:text-lg font-bold">{profileReport.confidence}%</span>
            <span className="text-xs text-blue-100 block text-center">confiance</span>
          </div>
        </div>

        {/* Quick Stats - Responsive grid */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <div className="bg-white bg-opacity-15 rounded-lg px-2 py-2 text-center">
            <p className="text-[10px] sm:text-xs text-blue-100">Population</p>
            <p className="text-xs sm:text-sm font-bold">{profileReport.population_percentage}</p>
          </div>
          <div className="bg-white bg-opacity-15 rounded-lg px-2 py-2 text-center">
            <p className="text-[10px] sm:text-xs text-blue-100">Score moyen</p>
            <p className="text-xs sm:text-sm font-bold">{profileReport.average_score}%</p>
          </div>
        </div>

        {/* View Report Button */}
        <button
          onClick={onOpenPdfViewer}
          disabled={generatingPdf}
          className="w-full bg-white text-primary font-semibold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-50 transition disabled:opacity-50"
        >
          {generatingPdf ? (
            <span className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></span>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          <span>Voir mon rapport complet</span>
        </button>

        {/* Download Link */}
        <button
          onClick={onDownloadPdf}
          className="w-full text-blue-200 text-sm py-2 mt-2 flex items-center justify-center gap-2 hover:text-white transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Télécharger le PDF
        </button>
      </div>
    </div>
  );
};

export default ProfileTab;
